





































import {Component, Vue, Watch, Inject} from 'vue-property-decorator';
import FlexGridModel, {
    Aggregate,
    AggregateRow,
    Column,
    Data,
    GroupRow,
    Item,
    MainRow,
    SubcomponentRow,
} from '@/models/FlexGridModel';
import FlexGridTableGroupRow from '@/components/base/flex-grid/table/FlexGridTableGroupRow.vue';
import FlexGridTableAggregateRow from '@/components/base/flex-grid/table/FlexGridTableAggregateRow.vue';
import FlexGridTableMainRow from '@/components/base/flex-grid/table/FlexGridTableMainRow.vue';
import FlexGridTableRowTypeEnum from '@/enums/FlexGridTableRowTypeEnum';
import FlexGridTableSubcomponentRow from '@/components/base/flex-grid/table/FlexGridTableSubcomponentRow.vue';
import Draggable from 'vuedraggable';
import {cloneDeep, isEqual} from 'lodash';
import {
    SET_DATA,
    SET_SELECTED_LIST,
    SET_TRANSPOSE,
    SET_TRANSPOSE_LOADING,
} from '@/store/modules/flexGridModule/flexGrid-mutation-types';

/**
 * @requires ./FlexGridTableGroupRow.vue
 * @requires ./FlexGridTableAggregateRow.vue
 * @requires ./FlexGridTableMainRow.vue
 * @requires ./FlexGridTableSubcomponentRow.vue
 */
@Component({
    components: {
        FlexGridTableSubcomponentRow,
        FlexGridTableMainRow,
        FlexGridTableAggregateRow,
        FlexGridTableGroupRow,
        Draggable,
    },
})
export default class FlexGridTableBody extends Vue {
    @Inject() moduleName!: string;
    @Inject() showZeros!: boolean;
    @Inject() commit!: Function;
    @Inject() urlPath!: string;
    @Inject() urlQuery!: Object;

    private items: any = null;
    private transposeItems: any = null;
    private rowTypes = FlexGridTableRowTypeEnum;

    get sortedData(): FlexGridModel | null {
        return this.$store.state[this.moduleName].sortedData;
    }

    get hasSubcomponents(): boolean {
        return this.$store.getters[`${this.moduleName}/hasSubcomponents`];
    }

    get columns() {
        let result: Array<Column> = [];
        if (!this.sortedData) return [];
        function forEachCallback(item: Column) {
            if (item.Columns) item.Columns.forEach(forEachCallback);
            else result.push(item);
        }
        this.sortedData.Columns.forEach(forEachCallback);
        return result;
    }

    get transpose(): boolean {
        return this.$store.state[this.moduleName].transpose;
    }

    get realItems() {
        return this.transpose ? this.transposeItems : this.items;
    }
    set realItems(value: any) {
        this.transposeItems = value;
    }

    get selectedList(): number[] {
        return this.$store.state[this.moduleName].selectedList;
    }

    get commonColspanStickyColumns() {
        if (this.sortedData?.Config.CountAnchoredColumns !== null) {
            return this.sortedData?.Columns.slice(0, this.sortedData.Config.CountAnchoredColumns).reduce(
                (summ, current) => (summ += current.ColSpan),
                0,
            );
        }
        return 0;
    }

    @Watch('sortedData', {deep: true, immediate: true})
    onDataChange(model: FlexGridModel | null, oldModel: FlexGridModel | null) {
        if (!model || isEqual(model, oldModel)) return;
        this.items = this.getItems(model.Data);
        this.transposeItems = cloneDeep(this.items);
    }

    @Watch('realItems', {deep: true, immediate: true})
    async onRealItemsChange() {
        this.$emit('update-fix-cells');
    }
    created() {
        this.$root.$on(`flex-grid${this.moduleName}:transpose-save`, this.onTransposeSave);
        this.$root.$on(`${this.moduleName}/selected-list:select-all`, this.onSelectAll);
        this.$root.$on(`${this.moduleName}/selected-list:reset`, this.onSelectReset);
    }

    destroyed() {
        this.$root.$off(`flex-grid${this.moduleName}:transpose-save`, this.onTransposeSave);
        this.$root.$off(`${this.moduleName}/selected-list:select-all`, this.onSelectAll);
        this.$root.$off(`${this.moduleName}/selected-list:reset`, this.onSelectReset);
    }

    getItems(data: Data) {
        let result: any = [];
        result = result.concat(this.getRows(data.Items));
        if (data.Aggregates.length > 0) result.push(this.getAggregateRow(data.Aggregates));
        return result;
    }

    getRows(data: Array<Item>) {
        let depth = 0,
            result: any = [];
        const forEachCallback = (item: Item, index: number, array: Array<Item>) => {
            if (index === 0) {
                depth += 1;
            }
            if (item.Group !== undefined && item.Items) {
                // групповая строка
                result.push(this.getGroupRow(item, depth));
                const groupIndex = result.length - 1;
                item.Items.forEach(forEachCallback);
                let slice = result.slice(groupIndex + 1);
                result[groupIndex].childsData = {
                    startIndex: groupIndex + 1,
                    endIndex: groupIndex + slice.length,
                    items: [...slice],
                };
            } else {
                // основаная строка с подкомпонентом
                const mainRow = this.getMainRow(item, depth);
                result.push(mainRow);
                if (this.hasSubcomponents) {
                    const subcomponent = this.getSubcomponentRow(item, depth + 1);
                    mainRow.subcomponent = subcomponent;
                    subcomponent.main = mainRow;
                    result.push(subcomponent);
                }
            }

            if (item.Aggregates && item.Aggregates.length > 0) {
                // агрегирующая строка
                result.push(this.getAggregateRow(item.Aggregates, depth));
            }

            if (index === array.length - 1) depth -= 1;
        };

        data.forEach(forEachCallback);
        return result;
    }

    getGroupRow(item: Item, depth: number): GroupRow {
        return {
            type: FlexGridTableRowTypeEnum.Group,
            text: item.Group,
            depth,
            isOpen: true,
            show: true,
        };
    }

    getMainRow(item: Item, depth: number): MainRow {
        const entries = Object.entries(item),
            cells: any = [];
        this.columns?.forEach((column, index) => {
            let row = entries.find(([key, value]) => key === column.Key);
            if (row) {
                const [key, value] = row;
                let text;
                if (column.Index !== undefined) {
                    if (!this.showZeros && value[column.Index] === 0) text = null;
                    else text = this.getFormatText(value[column.Index], String(column?.Precision));
                } else {
                    if (!this.showZeros && value === 0) text = null;
                    else text = this.getFormatText(value, String(column?.Precision));
                }
                cells.push({
                    text,
                    type: column.Type,
                    sticky: index < Number(this.commonColspanStickyColumns),
                    lastSticky: index === Number(this.commonColspanStickyColumns) - 1,
                });
            }
        });
        return {
            type: FlexGridTableRowTypeEnum.Main,
            cells,
            depth,
            isOpen: false,
            show: true,
            id: item.Id,
            data: {...item},
            selected: false,
        };
    }

    getAggregateRow(items: Array<Aggregate>, depth: number = 0): AggregateRow {
        const cells: any = [];
        this.columns?.forEach((column, index) => {
            let aggregate;
            if (column.Index !== undefined) {
                aggregate = items.find(item => item.Key === column.Key && item.Index === column.Index);
            } else {
                aggregate = items.find(item => item.Key === column.Key);
            }
            let cell: any = {
                sticky: index < Number(this.commonColspanStickyColumns),
                lastSticky: index === Number(this.commonColspanStickyColumns) - 1,
            };
            if (aggregate) {
                let text;
                if (!this.showZeros && aggregate.Aggregate === 0) text = null;
                else text = this.getFormatText(aggregate.Aggregate, String(aggregate.Precision));
                cell = {
                    ...cell,
                    text,
                    type: column.Type,
                };
            }
            cells.push(cell);
        });
        return {
            type: FlexGridTableRowTypeEnum.Aggregate,
            cells,
            depth,
            show: true,
        };
    }

    getSubcomponentRow(item: any, depth: number): SubcomponentRow {
        return {
            type: FlexGridTableRowTypeEnum.Subcomponent,
            depth,
            show: false,
            parent: item,
        };
    }

    getFormatText(value: string | number | boolean | null, presicion?: string): any {
        if (typeof value === 'number' && presicion && presicion !== 'undefined') {
            return this.$n(value, {
                minimumFractionDigits: presicion,
                maximumFractionDigits: presicion,
            });
        }
        return value;
    }

    async onTransposeSave() {
        const ids = this.transposeItems.map((item: any) => item.data.Id);
        this.commit(SET_TRANSPOSE_LOADING, true);
        try {
            const {data} = await this.$http.post(
                `${this.urlPath}/updateOrder`,
                {ids},
                {
                    params: {
                        ...this.urlQuery,
                    },
                },
            );
            this.commit(SET_DATA, data);
            this.commit(SET_TRANSPOSE, false);
        } finally {
            this.commit(SET_TRANSPOSE_LOADING, false);
        }
    }

    onSelectedListUpdate(data: any) {
        const bool = !data.selected;
        const item = this.items.find((item: any) => item.id === data.id);
        item.selected = bool;
        if (bool) {
            this.commit(SET_SELECTED_LIST, [...this.selectedList, data.id]);
        } else {
            this.commit(
                SET_SELECTED_LIST,
                this.selectedList.filter(id => id !== data.id),
            );
        }
    }

    onSelectAll() {
        this.items.forEach((item: any) => {
            if (item.type === FlexGridTableRowTypeEnum.Main) item.selected = true;
        });
        this.commit(
            SET_SELECTED_LIST,
            this.items.filter((item: any) => item.type === FlexGridTableRowTypeEnum.Main).map((item: any) => item.id),
        );
    }
    onSelectReset() {
        this.items.forEach((item: any) => (item.selected = false));
        this.commit(SET_SELECTED_LIST, []);
    }
}
